/**
 * Play Pass - client-side behavior.
 *   - QR code rendering (uses qrcode-generator from CDN)
 *   - Referral link copy + native share
 *
 * Loaded once per page from sections/sp-loyalty-rewards-member.liquid.
 */
(function () {
  'use strict';

  if (window.__solePlayLoyaltyLoaded) return;
  window.__solePlayLoyaltyLoaded = true;

  // ---------------------------------------------------------------------------
  // QR code rendering
  // ---------------------------------------------------------------------------
  var QR_CDN = 'https://cdn.jsdelivr.net/npm/qrcode-generator@1.4.4/qrcode.min.js';
  var qrLibLoading = null;

  function loadQrLib() {
    if (typeof window.qrcode === 'function') return Promise.resolve();
    if (qrLibLoading) return qrLibLoading;
    qrLibLoading = new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = QR_CDN;
      s.async = true;
      s.onload = function () { resolve(); };
      s.onerror = reject;
      document.head.appendChild(s);
    });
    return qrLibLoading;
  }

  function renderQRCodes(scope) {
    var root = scope || document;
    var nodes = root.querySelectorAll('[data-sp-qr]:not([data-sp-qr-rendered])');
    if (!nodes.length) return;

    loadQrLib().then(function () {
      nodes.forEach(function (el) {
        var url = el.getAttribute('data-url');
        if (!url) return;
        try {
          var qr = window.qrcode(0, 'M');
          qr.addData(url);
          qr.make();
          el.innerHTML = qr.createSvgTag({ cellSize: 4, margin: 2, scalable: true });
          var svg = el.querySelector('svg');
          if (svg) {
            svg.setAttribute('width', '100%');
            svg.setAttribute('height', '100%');
            svg.style.display = 'block';
          }
          el.setAttribute('data-sp-qr-rendered', '1');
        } catch (err) {
          if (window.console) console.warn('sp-loyalty-qr render failed', err);
        }
      });
    }).catch(function () {});
  }

  // ---------------------------------------------------------------------------
  // Referral copy / share
  // ---------------------------------------------------------------------------
  function flashToast(toast, msg) {
    if (!toast) return;
    toast.textContent = msg;
    toast.classList.add('is-visible');
    clearTimeout(toast.__t);
    toast.__t = setTimeout(function () {
      toast.classList.remove('is-visible');
    }, 2400);
  }

  function fallbackCopy(input, text) {
    if (!input) {
      window.prompt('Copy your referral link:', text);
      return false;
    }
    input.removeAttribute('readonly');
    input.select();
    input.setSelectionRange(0, text.length);
    var ok = false;
    try {
      ok = document.execCommand('copy');
    } catch (_) {}
    input.setAttribute('readonly', 'readonly');
    input.blur();
    return ok;
  }

  function copyToClipboard(text, onSuccess, onFail, input) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(onSuccess).catch(function () {
        if (fallbackCopy(input, text)) onSuccess(); else onFail();
      });
    } else {
      if (fallbackCopy(input, text)) onSuccess(); else onFail();
    }
  }

  function bindReferral(root) {
    if (root.hasAttribute('data-sp-referral-bound')) return;
    root.setAttribute('data-sp-referral-bound', '1');

    var url = root.getAttribute('data-url') || '';
    if (!url) return;

    var shareTitle = root.getAttribute('data-share-title') || '';
    var shareText = root.getAttribute('data-share-text') || '';
    var copySuccess = root.getAttribute('data-copy-success') || 'Link copied';

    var input = root.querySelector('[data-sp-referral-input]');
    var copyBtn = root.querySelector('[data-sp-referral-copy]');
    var copyLabel = root.querySelector('[data-sp-referral-copy-label]');
    var shareBtn = root.querySelector('[data-sp-referral-share]');
    var toast = root.querySelector('[data-sp-referral-toast]');

    function onCopied() {
      flashToast(toast, copySuccess);
      if (copyLabel) {
        var original = copyLabel.textContent;
        copyLabel.textContent = copySuccess;
        setTimeout(function () { copyLabel.textContent = original; }, 1800);
      }
    }
    function onCopyFail() {
      window.prompt('Copy your referral link:', url);
    }

    if (copyBtn) {
      copyBtn.addEventListener('click', function () {
        copyToClipboard(url, onCopied, onCopyFail, input);
      });
    }
    if (input) {
      input.addEventListener('focus', function () { input.select(); });
    }
    if (shareBtn) {
      shareBtn.addEventListener('click', function () {
        if (navigator.share) {
          navigator.share({ title: shareTitle, text: shareText, url: url }).catch(function () {});
        } else {
          copyToClipboard(url, onCopied, onCopyFail, input);
        }
      });
    }
  }

  function bindAllReferrals(scope) {
    var root = scope || document;
    var nodes = root.querySelectorAll('[data-sp-referral]');
    for (var i = 0; i < nodes.length; i++) bindReferral(nodes[i]);
  }

  // ---------------------------------------------------------------------------
  // Featured products carousel — arrow controls + state sync
  // ---------------------------------------------------------------------------
  function bindCarousel(root) {
    if (root.hasAttribute('data-sp-carousel-bound')) return;
    root.setAttribute('data-sp-carousel-bound', '1');

    var track = root.querySelector('[data-sp-carousel-track]');
    var prev = root.querySelector('[data-sp-carousel-prev]');
    var next = root.querySelector('[data-sp-carousel-next]');
    if (!track) return;

    function stepSize() {
      var first = track.querySelector('.sp-featured__item');
      if (!first) return track.clientWidth;
      var rect = first.getBoundingClientRect();
      var styles = window.getComputedStyle(track);
      var gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
      return rect.width + gap;
    }

    function updateArrows() {
      if (!prev || !next) return;
      var maxScroll = track.scrollWidth - track.clientWidth - 1;
      var atStart = track.scrollLeft <= 1;
      var atEnd = track.scrollLeft >= maxScroll;
      prev.toggleAttribute('disabled', atStart);
      next.toggleAttribute('disabled', atEnd);
    }

    if (prev) {
      prev.addEventListener('click', function () {
        track.scrollBy({ left: -stepSize(), behavior: 'smooth' });
      });
    }
    if (next) {
      next.addEventListener('click', function () {
        track.scrollBy({ left: stepSize(), behavior: 'smooth' });
      });
    }

    track.addEventListener('scroll', function () {
      clearTimeout(track.__arrT);
      track.__arrT = setTimeout(updateArrows, 80);
    }, { passive: true });

    window.addEventListener('resize', function () {
      clearTimeout(track.__arrT);
      track.__arrT = setTimeout(updateArrows, 120);
    });

    updateArrows();
  }

  function bindAllCarousels(scope) {
    var root = scope || document;
    var nodes = root.querySelectorAll('[data-sp-carousel]');
    for (var i = 0; i < nodes.length; i++) bindCarousel(nodes[i]);
  }

  // ---------------------------------------------------------------------------
  // Hero device live clock
  // ---------------------------------------------------------------------------
  function formatClockTime(date, timeZone) {
    var formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timeZone || 'America/New_York',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });

    if (typeof formatter.formatToParts === 'function') {
      return formatter.formatToParts(date).filter(function (part) {
        return part.type === 'hour' || part.type === 'minute' || part.type === 'literal';
      }).map(function (part) {
        return part.value;
      }).join('').trim();
    }

    return formatter.format(date).replace(/\s?[AP]\.?M\.?$/i, '');
  }

  function updateLiveClock(node) {
    var timeZone = node.getAttribute('data-sp-live-clock-time-zone') || 'America/New_York';
    var now = new Date();

    try {
      node.textContent = formatClockTime(now, timeZone);
    } catch (_) {
      node.textContent = formatClockTime(now, 'America/New_York');
    }

    node.setAttribute('datetime', now.toISOString());
  }

  function bindLiveClock(node) {
    if (node.hasAttribute('data-sp-live-clock-bound')) return;
    node.setAttribute('data-sp-live-clock-bound', '1');

    function tick() {
      updateLiveClock(node);
      var delay = (60 - new Date().getSeconds()) * 1000;
      node.__spLiveClockTimer = window.setTimeout(tick, delay);
    }

    tick();
  }

  function bindAllLiveClocks(scope) {
    var root = scope || document;
    var nodes = root.querySelectorAll('[data-sp-live-clock]');
    for (var i = 0; i < nodes.length; i++) bindLiveClock(nodes[i]);
  }

  // ---------------------------------------------------------------------------
  // Init + re-init on Shopify section events
  // ---------------------------------------------------------------------------
  function init(scope) {
    renderQRCodes(scope);
    bindAllReferrals(scope);
    bindAllCarousels(scope);
    bindAllLiveClocks(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); }, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (e) {
    init(e.target || document);
  });
  document.addEventListener('shopify:block:select', function (e) {
    init(e.target || document);
  });
})();

/* ----------------------------------------------------------------------------
   Claim handle — captures a member's gaming handle for custom.username.
   IMPORTANT: a Shopify theme CANNOT write a customer metafield directly, so the
   actual persistence needs a server-side intermediary (app proxy / app). Point
   data-sp-handle-endpoint at that endpoint; it should accept JSON {handle,
   namespace, key}, write the customer's metafield, and return 2xx. Until an
   endpoint is configured the form validates and shows a friendly message.
---------------------------------------------------------------------------- */
(function () {
  function initHandle(root) {
    var block = (root || document).querySelector('[data-sp-handle]');
    if (!block || block.dataset.spHandleReady) return;
    block.dataset.spHandleReady = '1';

    var form = block.querySelector('[data-sp-handle-form]');
    var input = block.querySelector('[data-sp-handle-input]');
    var btn = block.querySelector('[data-sp-handle-submit]');
    var msg = block.querySelector('[data-sp-handle-msg]');
    if (!form || !input || !btn) return;

    var endpoint = block.getAttribute('data-sp-handle-endpoint') || '';

    function setMsg(text, type) {
      if (!msg) return;
      msg.textContent = text;
      msg.hidden = false;
      msg.classList.remove('sp-handle__msg--error', 'sp-handle__msg--ok');
      if (type) msg.classList.add('sp-handle__msg--' + type);
    }

    function valid(h) { return /^[a-zA-Z0-9_.\-]{3,20}$/.test(h); }

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var handle = (input.value || '').trim();
      if (!valid(handle)) {
        setMsg('Handles are 3–20 characters: letters, numbers, _ . -', 'error');
        input.focus();
        return;
      }
      if (!endpoint) {
        setMsg('Handle saving is being connected — check back soon.', 'error');
        return;
      }
      btn.disabled = true;
      setMsg('Saving…');
      fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          handle: handle,
          namespace: block.getAttribute('data-sp-handle-namespace') || 'custom',
          key: block.getAttribute('data-sp-handle-key') || 'username'
        })
      }).then(function (r) {
        if (!r.ok) throw new Error('save_failed');
        setMsg('Handle locked in. Welcome, ' + handle + '.', 'ok');
        setTimeout(function () { window.location.reload(); }, 800);
      }).catch(function () {
        btn.disabled = false;
        setMsg('Could not save right now. Please try again.', 'error');
      });
    });
  }

  if (document.readyState !== 'loading') initHandle(document);
  else document.addEventListener('DOMContentLoaded', function () { initHandle(document); });
  document.addEventListener('shopify:section:load', function (e) { initHandle(e.target || document); });
})();

/* ----------------------------------------------------------------------------
   Play Pass XP bar — activate + animate (fill grows, number counts up) on the
   first page load for signed-in members. Logged out stays drained at 0.
---------------------------------------------------------------------------- */
(function () {
  function fmt(n) {
    n = Math.round(n);
    try { return n.toLocaleString(); } catch (e) { return '' + n; }
  }
  function initXP(root) {
    var el = (root || document).querySelector('[data-sp-xp]');
    if (!el || el.dataset.spXpReady) return;
    el.dataset.spXpReady = '1';

    var active = el.getAttribute('data-sp-xp-active') === '1';
    var value = parseFloat(el.getAttribute('data-sp-xp-value')) || 0;
    var max = parseFloat(el.getAttribute('data-sp-xp-max')) || 1;
    var fill = el.querySelector('[data-sp-xp-fill]');
    var num = el.querySelector('[data-sp-xp-num]');
    var lvl = el.querySelector('.sp-xp__lvl');
    if (!active) {
      if (fill) fill.style.width = '0%';
      if (num) num.textContent = '0';
      return;
    }

    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // The label flips from the starting level up to the earned tier as the bar lands.
    var toLvl = lvl ? lvl.textContent.trim() : '';
    var fromLvl = (el.getAttribute('data-sp-xp-from') || 'Lvl. 0').toUpperCase();
    // Reset to the "from" level immediately so the climb is visible from first paint.
    if (lvl) lvl.textContent = fromLvl;

    function pop() {
      el.classList.add('sp-xp--pop');
      setTimeout(function () { el.classList.remove('sp-xp--pop'); }, 850);
    }

    function levelUp() {
      if (lvl && toLvl) lvl.textContent = toLvl;
      pop();
      if (reduce) return;
      el.classList.add('sp-xp--levelup');
      setTimeout(function () { el.classList.remove('sp-xp--levelup'); }, 1900);
    }

    function animate() {
      // Bar fills all the way — then you level up. Fire the celebration when it lands.
      if (fill) {
        if (reduce) {
          fill.style.width = '100%';
          levelUp();
        } else {
          fill.addEventListener('transitionend', function onEnd(e) {
            if (e.propertyName !== 'width') return;
            fill.removeEventListener('transitionend', onEnd);
            levelUp();
          });
          fill.style.width = '100%';
        }
      }
      if (!num) return;
      if (reduce) { num.textContent = fmt(value); return; }
      var start = null;
      var dur = 1500;
      function step(ts) {
        if (start === null) start = ts;
        var t = Math.min(1, (ts - start) / dur);
        var eased = 1 - Math.pow(1 - t, 3);
        num.textContent = fmt(value * eased);
        if (t < 1) requestAnimationFrame(step);
      }
      requestAnimationFrame(step);
    }

    // brief delay so the activation/growth is visible on first paint
    setTimeout(function () { requestAnimationFrame(animate); }, reduce ? 0 : 300);
  }

  if (document.readyState !== 'loading') initXP(document);
  else document.addEventListener('DOMContentLoaded', function () { initXP(document); });
  document.addEventListener('shopify:section:load', function (e) { initXP(e.target || document); });
})();

/* ----------------------------------------------------------------------------
   Auto-refresh — when a signed-in member's Angle metafields aren't provisioned
   yet (wallet pass link or referral link still blank right after enrolling),
   poll the Section Rendering API and hot-swap the section in as soon as the
   data lands, so the member never has to reload. A "syncing" pill shows while
   we wait. Bounded backoff (~4.5 min) then gives up quietly.
---------------------------------------------------------------------------- */
(function () {
  if (!window.fetch || !window.DOMParser) return;
  // backoff schedule in ms — quick at first, then easing off
  var SCHEDULE = [3000, 3000, 4000, 5000, 6000, 8000, 10000, 12000, 15000, 20000, 30000, 30000, 45000, 60000];

  function startPolling(section) {
    if (!section || section.dataset.spArReady) return;
    section.dataset.spArReady = '1';
    var id = section.getAttribute('data-sp-section-id');
    if (!id) return;

    // The pending state already renders an in-place skeleton (QR/link shimmer);
    // we just poll and swap the real content in when it's ready.
    section.classList.add('sp-section--syncing');

    var attempt = 0;

    function stopUi() {
      section.classList.remove('sp-section--syncing');
    }

    function next() {
      if (attempt >= SCHEDULE.length) { stopUi(); return; } // give up quietly
      var delay = SCHEDULE[attempt];
      attempt++;
      setTimeout(poll, delay);
    }

    function poll() {
      var url = location.pathname + '?section_id=' + encodeURIComponent(id) + '&_cb=' + Date.now();
      fetch(url, { credentials: 'same-origin', headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then(function (r) { return r.ok ? r.text() : Promise.reject(r.status); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var fresh = doc.querySelector('[data-sp-section-id="' + id + '"]') || doc.querySelector('[data-sp-autorefresh]');
          if (fresh && fresh.getAttribute('data-sp-autorefresh') === '0') {
            swap(fresh);
          } else {
            next();
          }
        })
        .catch(function () { next(); });
    }

    function swap(freshEl) {
      stopUi();
      var live = document.querySelector('[data-sp-section-id="' + id + '"]');
      if (!live) return;
      live.outerHTML = freshEl.outerHTML;
      var swapped = document.querySelector('[data-sp-section-id="' + id + '"]');
      if (swapped) {
        // re-init the section's widgets (QR, referral copy/share, etc.)
        try { swapped.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true })); } catch (e) {}
      }
    }

    next();
  }

  function initAutoRefresh(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var nodes = scope.querySelectorAll('[data-sp-autorefresh="1"]');
    for (var i = 0; i < nodes.length; i++) startPolling(nodes[i]);
    if (root && root.getAttribute && root.getAttribute('data-sp-autorefresh') === '1') startPolling(root);
  }

  if (document.readyState !== 'loading') initAutoRefresh(document);
  else document.addEventListener('DOMContentLoaded', function () { initAutoRefresh(document); });
  document.addEventListener('shopify:section:load', function (e) { initAutoRefresh(e.target || document); });
})();
